import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, ChevronLeft, ChevronRight, ShieldCheck, Droplet, Wrench, Sparkles, Clock, IndianRupee } from "lucide-react";
import { InquiryForm } from "@/components/ur/InquiryForm";
import { IMAGES, BRAND } from "@/data/urban";
import { FinalCTA } from "@/components/ur/FinalCTA";

const GALLERY = [
  { img: IMAGES.bathroom1, title: "Warm Terracotta Spa", tag: "Contemporary · Wood-slat + Terrazzo", desc: "Fluted teak feature wall, terrazzo floor, hammered copper vessel sink and brushed brass rain shower. Diffused LED cove lights and a wall-hung WC keep the floor visually open." },
  { img: IMAGES.bathroom2, title: "Blush & Brass Luxury", tag: "Luxury · Beige Marble + Brass", desc: "Full-height beige-marble cladding, blush lacquer overhead cabinet and brushed-brass rainfall shower with in-wall storage niche. A back-lit oval mirror finishes the vanity." },
  { img: IMAGES.bathroom3, title: "Patterned-Tile Bathroom", tag: "Modern · Moroccan Tile + Terrazzo", desc: "Moroccan feature wall behind a black-framed walk-in shower, walnut fluted vanity with matte-black tap-ware and a heated towel rail." },
  { img: IMAGES.bathroom4, title: "Grey Stone & Oak", tag: "Minimal · Stone + Oak + Matte Black", desc: "Concrete-look grey stone tiles with oak overhead storage, a floating black-oak vanity and matte-black fixtures. Warm LED cove lighting adds softness." },
];

const TIERS = [
  {
    name: "Essential",
    price: "₹85,000",
    per: "onwards / bathroom",
    tag: "Refresh · 1–2 weeks",
    desc: "Perfect for a quick makeover — same layout, better everything.",
    features: [
      "Wall + floor tile replacement (upto 100 sq.ft)",
      "1 waterproofing coat under new tiles",
      "Standard CP fittings (Jaquar Continental or equal)",
      "Wall-mounted WC + wash basin",
      "1 shower area with plain glass partition",
      "False ceiling: plain gypsum with LED lights",
      "Painting inside bathroom + basic electrical",
    ],
  },
  {
    name: "Premium",
    price: "₹1,50,000",
    per: "onwards / bathroom",
    tag: "Most popular · 2–3 weeks",
    highlight: true,
    desc: "Layout tweaks, designer tiles, brand CP fittings and vanity storage.",
    features: [
      "Everything in Essential",
      "Designer imported feature-wall tile",
      "Complete plumbing rework + 2-coat waterproofing",
      "Premium CP fittings (Kohler / Jaquar Opal / Grohe)",
      "Rain shower + hand shower diverter",
      "Floating vanity with laminate + solid-surface top",
      "Backlit LED mirror + niche shelf",
      "Under-tile heated water outlets ready",
    ],
  },
  {
    name: "Luxury",
    price: "₹3,00,000",
    per: "onwards / bathroom",
    tag: "Full spa · 3–5 weeks",
    desc: "Marble, brass, integrated storage and a full spa experience.",
    features: [
      "Everything in Premium",
      "Full-height Italian marble or large-format porcelain",
      "Concealed cistern with brass flush plate",
      "Brushed-brass or matte-black designer CP suite",
      "Custom veneer vanity with copper / stone basin",
      "Walk-in shower with frameless 10mm toughened glass",
      "Terrazzo / heated flooring option",
      "Smart mirror + LED cove + speaker-ready ceiling",
    ],
  },
];

const INCLUSIONS = [
  { icon: Droplet, title: "2-Coat Waterproofing", desc: "Every renovation includes membrane + chemical waterproofing before tiling." },
  { icon: Wrench, title: "Plumbing & Electrical", desc: "Licensed plumbers and electricians — new lines, tested pressure & load." },
  { icon: ShieldCheck, title: "10-Year Waterproofing Warranty", desc: "On our recommended waterproofing systems, in writing." },
  { icon: Sparkles, title: "CP Fittings from Brand Stock", desc: "Jaquar, Kohler, Grohe or equivalent — no local unbranded parts." },
  { icon: Clock, title: "Fixed Timelines", desc: "Locked completion dates with weekly progress reports on WhatsApp." },
  { icon: IndianRupee, title: "Itemised Quotation", desc: "No 'lump-sum' pricing — every tile, fitting and hour is on paper." },
];

const FAQS = [
  { q: "How long does a bathroom renovation take?", a: "A full renovation typically takes 2–4 weeks depending on tile choice, plumbing rework and delivery of imported fittings. The Essential tier can finish in 10–12 working days if tiles are in stock." },
  { q: "Do you handle plumbing and waterproofing yourself?", a: "Yes. Both are handled by our in-house licensed plumbers and waterproofing specialists — never subcontracted blindly. Waterproofing is done before the first tile is laid." },
  { q: "Can I keep some existing fittings?", a: "Absolutely. If your current WC, faucets or shower panel is in good shape, we credit that back in the quote and only replace what's needed." },
  { q: "Do you supply tiles and CP fittings or should I buy them?", a: "Both options are available. Our design team can select and supply from our vendor network at trade prices, or we can install materials you've bought — with clear responsibility for waterproofing either way." },
  { q: "What warranty do I get?", a: "10-year waterproofing warranty (on our recommended systems), 1-year workmanship warranty and the manufacturer warranty on all CP fittings — all documented in the final invoice." },
];

const Lightbox = ({ items, index, onClose, onPrev, onNext }) => {
  const item = items[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col" role="dialog" aria-modal="true" data-testid="bathroom-lightbox">
      <div className="flex items-center justify-between px-5 py-4 text-white/80">
        <div className="text-xs uppercase tracking-[0.24em]">{index + 1} / {items.length}</div>
        <button onClick={onClose} data-testid="bathroom-lightbox-close" aria-label="Close" className="h-10 w-10 rounded-full border border-white/25 flex items-center justify-center hover:border-white hover:text-white transition-colors">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-6 relative">
        <button onClick={onPrev} aria-label="Previous" data-testid="bathroom-lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-white/25 text-white/80 flex items-center justify-center hover:text-white hover:border-white transition-colors">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="max-w-[1100px] w-full flex flex-col items-center gap-4">
          <img src={item.img} alt={item.title} className="max-h-[70vh] w-auto object-contain rounded-md" />
          <div className="max-w-2xl text-center text-white">
            <div className="overline text-white/60">{item.tag}</div>
            <h3 className="mt-2 font-heading text-2xl font-extrabold">{item.title}</h3>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">{item.desc}</p>
          </div>
        </div>
        <button onClick={onNext} aria-label="Next" data-testid="bathroom-lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-white/25 text-white/80 flex items-center justify-center hover:text-white hover:border-white transition-colors">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export const BathroomRenovation = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const openAt = useCallback((i) => setOpenIdx(i), []);
  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(() => setOpenIdx((i) => (i == null ? i : (i + GALLERY.length - 1) % GALLERY.length)), []);
  const next = useCallback(() => setOpenIdx((i) => (i == null ? i : (i + 1) % GALLERY.length)), []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const prevTitle = document.title;
    document.title = "Bathroom Renovation in Gurgaon | Urban Renovations";
    const desc = "Turnkey bathroom renovation in Gurgaon — waterproofing, plumbing, designer tiles, brand CP fittings & false ceiling. Pricing from ₹85,000. 10-year waterproofing warranty.";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || null;
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "description"); document.head.appendChild(meta); }
    meta.setAttribute("content", desc);
    return () => { document.title = prevTitle; if (meta && prevDesc != null) meta.setAttribute("content", prevDesc); };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-24 pb-14 bg-black text-white" data-testid="bathroom-hero">
        <div className="absolute inset-0 opacity-45"><img src={IMAGES.bathroom1} alt="Bathroom Renovation Gurgaon" className="h-full w-full object-cover" /></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/10" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-7">
            <div className="overline text-white/70">Bathroom Renovation · Gurgaon · Delhi NCR</div>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">Spa-grade bathrooms, delivered turnkey.</h1>
            <div className="mt-4 font-heading text-2xl font-extrabold">Starting from ₹85,000 / bathroom</div>
            <p className="mt-4 text-white/80 max-w-xl leading-relaxed">Waterproofing, plumbing rework, designer tiles, brand CP fittings, false ceiling & painting — all under one accountable contract with a 10-year waterproofing warranty.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="bathroom-hero-cta" className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-white/90">Get Free Site Visit <ArrowRight className="h-4 w-4" /></Link>
              <a href="#bathroom-gallery" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black">View Designs</a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <InquiryForm heading="Get a Free Quote" subheading="Free site visit · Reply within 24 hrs" source="bathroom-renovation-page" defaultService="Bathroom Renovation" defaultBudget="₹1L – ₹3L" dark testId="bathroom-inquiry" />
          </div>
        </div>
      </section>

      {/* INTRO / SEO copy */}
      <section className="py-20 bg-white border-t border-hairline">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-ink-soft">Why Urban Renovations</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink leading-tight">The only bathroom quote you'll take seriously.</h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-ink-soft leading-relaxed">A bathroom renovation is a plumbing project first and a design project second. That's why every project at Urban Renovations begins with a licensed plumber and a two-coat waterproofing system — not a mood board. Once the water-tight core is done, we layer designer tiles, brand-name CP fittings, custom vanities and false ceilings to create bathrooms you'd expect at a boutique hotel — for a fraction of the cost.</p>
            <p className="mt-5 text-ink-soft leading-relaxed">Whether you're refreshing a single builder-finish bathroom in DLF or gutting three bathrooms for a full-home renovation in Golf Course Road, we deliver on fixed timelines with itemised pricing and a 10-year waterproofing warranty in writing.</p>
          </div>
        </div>
      </section>

      {/* PRICING TIERS */}
      <section className="py-20 bg-canvas border-t border-hairline" id="bathroom-pricing" data-testid="bathroom-pricing">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="overline text-ink-soft">Pricing</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink leading-tight">Three transparent tiers, zero surprises.</h2>
            <p className="mt-3 text-sm text-ink-soft">Every tier is a fixed-scope, itemised package. Extra work — if any — is priced and approved by you before it starts.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((t) => (
              <div key={t.name} data-testid={`bathroom-tier-${t.name.toLowerCase()}`} className={`relative rounded-2xl p-8 flex flex-col ${t.highlight ? "bg-black text-white" : "bg-white text-ink border border-hairline"}`}>
                {t.highlight && (
                  <div className="absolute -top-3 left-8 rounded-full bg-white text-black px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em]">Most Popular</div>
                )}
                <div className={`overline ${t.highlight ? "text-white/70" : "text-ink-soft"}`}>{t.tag}</div>
                <div className="mt-3 font-heading text-2xl font-extrabold">{t.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <div className="font-heading text-3xl font-extrabold">{t.price}</div>
                  <div className={`text-xs ${t.highlight ? "text-white/70" : "text-ink-soft"}`}>{t.per}</div>
                </div>
                <p className={`mt-4 text-sm ${t.highlight ? "text-white/80" : "text-ink-soft"}`}>{t.desc}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${t.highlight ? "text-white/90" : "text-ink"}`}>
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${t.highlight ? "text-white" : "text-ink"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`bathroom-tier-cta-${t.name.toLowerCase()}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-wider ${t.highlight ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-ink"}`}
                >
                  Choose {t.name} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-ink-soft italic">Prices are indicative starting prices for a standard 4×6 ft bathroom and may vary based on layout, size, materials, finish, hardware and accessories.</p>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 bg-white border-t border-hairline" id="bathroom-gallery" data-testid="bathroom-gallery">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-xl">
              <div className="overline text-ink-soft">Bathroom Gallery</div>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink leading-tight">Real bathrooms, real finishes.</h2>
              <p className="mt-3 text-sm text-ink-soft">Tap any image for a closer look.</p>
            </div>
            <Link to="/designs" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink hover:opacity-70">All Designs <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {GALLERY.map((g, i) => (
              <button
                key={g.title}
                type="button"
                onClick={() => openAt(i)}
                data-testid={`bathroom-gallery-item-${i}`}
                className="group rounded-2xl overflow-hidden bg-canvas border border-hairline hover:border-ink transition-colors text-left"
              >
                <div className="aspect-[4/5] overflow-hidden"><img src={g.img} alt={g.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-ink-soft">{g.tag}</div>
                  <h3 className="mt-1.5 font-heading text-base font-bold text-ink">{g.title}</h3>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 bg-canvas border-t border-hairline">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="overline text-ink-soft">What's Included</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink leading-tight">Every project, standard.</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUSIONS.map((s) => { const Ic = s.icon; return (
              <div key={s.title} className="rounded-2xl bg-white border border-hairline p-6">
                <span className="h-10 w-10 rounded-md bg-black text-white flex items-center justify-center"><Ic className="h-5 w-5" /></span>
                <h3 className="mt-4 font-heading font-bold text-ink">{s.title}</h3>
                <p className="mt-1.5 text-sm text-ink-soft">{s.desc}</p>
              </div>
            ); })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white border-t border-hairline" data-testid="bathroom-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="overline text-ink-soft">FAQ</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-ink leading-tight">Bathroom renovation — the honest answers.</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <details key={f.q} className="group rounded-xl bg-canvas border border-hairline p-5" data-testid={`bathroom-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 text-ink font-semibold">
                  <span>{f.q}</span>
                  <span className="ml-4 mt-0.5 h-6 w-6 rounded-full border border-hairline flex items-center justify-center text-ink group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-sm text-ink-soft">
            Still have questions? Call us directly at <a href={`tel:${BRAND.phoneRaw}`} className="text-ink font-bold underline underline-offset-4">{BRAND.phone}</a>.
          </div>
        </div>
      </section>

      <FinalCTA />

      {openIdx != null && (
        <Lightbox items={GALLERY} index={openIdx} onClose={close} onPrev={prev} onNext={next} />
      )}
    </>
  );
};

export default BathroomRenovation;
